import type { IComponentController, IScope } from "angular";
import { finalize } from "rxjs/operators";

import type {
    IConfigurationGroup,
    IConfigurationGroupCare,
    IConfigurationGroupDetection,
    IConfigurationGroupMonitoring,
    IConfigurationGroupRecording,
    IResponseWithLoading,
    ISite,
} from "App/Modules/Models";
import { ConfigurationGroupType } from "App/Modules/Models";
import type { IConfigurationGroupReporting } from "App/Modules/Models/Models/IConfigurationGroupReporting";
import type { ISitesRepository } from "App/Modules/Repositories";
import type { IModalService } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services";

export default class SiteConfigurationGroupsComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "mmModalService", "mmSitesRepository", "$translate"];

    public configurationGroups: IResponseWithLoading<IConfigurationGroup[]>;

    public site: IResponseWithLoading<ISite>;

    public siteId: string;

    constructor(
        private readonly $scope: IScope,
        private readonly modalService: IModalService,
        private readonly sitesRepository: ISitesRepository,
        private readonly $translate: ng.translate.ITranslateService,
    ) {}

    public $onInit() {
        extendWithLoadingAndFail(this.sitesRepository.getSite(this.siteId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (result) => {
                    this.site = result;
                },
                (result) => {
                    this.site = result;
                },
            );

        extendWithLoadingAndFail(this.sitesRepository.getConfigurationGroups(this.siteId))
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe(
                (result) => {
                    this.configurationGroups = result;
                },
                (result) => {
                    this.configurationGroups = result;
                },
            );
    }

    public addConfigurationGroup() {
        this.modalService
            .open<IConfigurationGroup, ISite>("mmSiteConfigurationGroupAdd", null, this.site.data)
            .subscribe(
                (addedConfigurationGroupResponse) => {
                    this.configurationGroups.data.push(addedConfigurationGroupResponse);

                    switch (addedConfigurationGroupResponse.type) {
                        case ConfigurationGroupType.Care:
                            this.editConfigurationGroupCare(addedConfigurationGroupResponse.id);
                            break;
                        case ConfigurationGroupType.Monitoring:
                            this.editConfigurationGroupMonitoring(addedConfigurationGroupResponse.id);
                            break;
                        case ConfigurationGroupType.Detection:
                            this.editConfigurationGroupDetection(addedConfigurationGroupResponse.id);
                            break;
                        case ConfigurationGroupType.Reporting:
                            this.editConfigurationGroupReporting(addedConfigurationGroupResponse.id);
                            break;
                        case ConfigurationGroupType.Recording:
                            this.editConfigurationGroupRecording(addedConfigurationGroupResponse.id);
                            break;
                        default:
                            break;
                    }
                },
                (reason) => reason,
            );
    }

    public deleteGroup(id: string) {
        this.configurationGroups.data = this.configurationGroups.data.filter((group) => {
            return group.id !== id;
        });
    }

    private editConfigurationGroupCare(addedCGId: string) {
        this.sitesRepository
            .getConfigurationGroupCare(this.siteId, addedCGId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((result) => {
                this.modalService
                    .open<IConfigurationGroupCare, void>("mmConfigurationGroupCareEdit", result.data, null, "lg")
                    .subscribe(
                        (editedConfiguration) => {
                            if (editedConfiguration !== null) {
                                result.data = editedConfiguration;

                                const index = this.configurationGroups.data.findIndex(
                                    (cgs) => cgs.id === result.data.id,
                                );

                                if (index !== -1) {
                                    this.configurationGroups.data[index] = result.data;
                                }
                            } else {
                                this.deleteGroup(result.data.id);
                            }
                        },
                        (reason) => reason,
                    );
            });
    }

    private editConfigurationGroupMonitoring(addedCGId: string) {
        this.sitesRepository
            .getConfigurationGroupMonitoring(this.siteId, addedCGId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((result) => {
                this.modalService
                    .open<IConfigurationGroupMonitoring, void>(
                        "mmConfigurationGroupMonitoringEdit",
                        result.data,
                        null,
                        "lg",
                    )
                    .subscribe(
                        (editedConfiguration) => {
                            if (editedConfiguration !== null) {
                                result.data = editedConfiguration;

                                const index = this.configurationGroups.data.findIndex(
                                    (cgs) => cgs.id === result.data.id,
                                );

                                if (index !== -1) {
                                    this.configurationGroups.data[index] = result.data;
                                }
                            } else {
                                this.deleteGroup(result.data.id);
                            }
                        },
                        (reason) => reason,
                    );
            });
    }

    private editConfigurationGroupDetection(addedCGId: string) {
        this.sitesRepository
            .getConfigurationGroupDetection(this.siteId, addedCGId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((result) => {
                this.modalService
                    .open<IConfigurationGroupDetection, void>(
                        "mmConfigurationGroupDetectionEdit",
                        result.data,
                        null,
                        "xlg",
                    )
                    .subscribe(
                        (editedConfiguration) => {
                            if (editedConfiguration !== null) {
                                result.data = editedConfiguration;

                                const index = this.configurationGroups.data.findIndex(
                                    (cgs) => cgs.id === result.data.id,
                                );
                                if (index !== -1) {
                                    this.configurationGroups.data[index] = result.data;
                                }
                            } else {
                                this.deleteGroup(result.data.id);
                            }
                        },
                        (reason) => reason,
                    );
            });
    }

    private editConfigurationGroupReporting(addedCGId: string) {
        this.sitesRepository
            .getConfigurationGroupReporting(this.siteId, addedCGId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((result) => {
                this.modalService
                    .open<IConfigurationGroupReporting, void>(
                        "mmConfigurationGroupReportingEdit",
                        result.data,
                        null,
                        "lg",
                    )
                    .subscribe(
                        (editedConfiguration) => {
                            if (editedConfiguration !== null) {
                                result.data = editedConfiguration;

                                const index = this.configurationGroups.data.findIndex(
                                    (cgs) => cgs.id === result.data.id,
                                );
                                if (index !== -1) {
                                    this.configurationGroups.data[index] = result.data;
                                }
                            } else {
                                this.deleteGroup(result.data.id);
                            }
                        },
                        (reason) => reason,
                    );
            });
    }

    private editConfigurationGroupRecording(addedCGId: string) {
        this.sitesRepository
            .getConfigurationGroupRecording(this.siteId, addedCGId)
            .pipe(finalize(() => this.$scope.$applyAsync()))
            .subscribe((result) => {
                this.modalService
                    .open<IConfigurationGroupRecording, void>(
                        "mmConfigurationGroupRecordingEdit",
                        result.data,
                        null,
                        "xlg",
                    )
                    .subscribe(
                        (editedConfiguration) => {
                            if (editedConfiguration !== null) {
                                result.data = editedConfiguration;

                                const index = this.configurationGroups.data.findIndex(
                                    (cgs) => cgs.id === result.data.id,
                                );
                                if (index !== -1) {
                                    this.configurationGroups.data[index] = result.data;
                                }
                            } else {
                                this.deleteGroup(result.data.id);
                            }
                        },
                        (reason) => reason,
                    );
            });
    }
}
